/**
 * Product Questions & Answers — fetches approved Q&A for the current product
 * from a Google Apps Script backend, renders them, and lets customers submit
 * new questions via AJAX (no page reload).
 */
(() => {
  const DEDUPE_STORAGE_KEY = "productQuestionsSubmitted";
  const VOTE_STORAGE_KEY = "productQuestionsVoted";
  const ANSWER_DEDUPE_STORAGE_KEY = "productQuestionsAnswerSubmitted";
  const HELPFUL_STORAGE_KEY = "productQuestionsAnswerHelpful";
  const SUBMIT_COOLDOWN_MS = 30000;

  class ProductQuestions extends HTMLElement {
    connectedCallback() {
      this.apiUrl = this.dataset.apiUrl || "";
      this.handle = this.dataset.productHandle || "";
      this.productTitle = this.dataset.productTitle || "";
      this.productUrl = this.dataset.productUrl || "";

      this.allQuestions = [];
      this.isSubmitting = false;

      this.listEl = this.querySelector("[data-question-list]");
      this.countEl = this.querySelector("[data-question-count]");
      this.emptyEl = this.querySelector("[data-empty-state]");
      this.statusEl = this.querySelector("[data-status]");
      this.searchInput = this.querySelector("[data-search-input]");
      this.formWrapper = this.querySelector("[data-form-wrapper]");
      this.form = this.querySelector("[data-question-form]");
      this.formMessage = this.querySelector("[data-form-message]");
      this.submitButton = this.querySelector("[data-submit-button]");
      this.rteEditor = this.querySelector("[data-rte-editor]");
      this.rteSource = this.querySelector("[data-rte-source]");

      // Answer Now panel
      this.listView = this.querySelector("[data-list-view]");
      this.answerPanel = this.querySelector("[data-answer-panel]");
      this.backToListButton = this.querySelector("[data-back-to-list]");
      this.detailQuestionEl = this.querySelector("[data-detail-question]");
      this.detailMetaEl = this.querySelector("[data-detail-meta]");
      this.answerForm = this.querySelector("[data-answer-form]");
      this.answerFormMessage = this.querySelector("[data-answer-form-message]");
      this.answerSubmitButton = this.querySelector("[data-answer-submit-button]");
      this.answersCountEl = this.querySelector("[data-answers-count]");
      this.answersListEl = this.querySelector("[data-answers-list]");
      this.answersEmptyEl = this.querySelector("[data-answers-empty]");
      this.sortButtons = this.querySelectorAll("[data-sort-type]");

      this.activeQuestion = null;
      this.activeSort = "useful";
      this.isSubmittingAnswer = false;

      this.bindEvents();
      this.setupRte();
      this.loadQuestions();
    }

    setupRte() {
      if (!this.rteEditor || !this.rteSource) return;

      const toolbar = this.querySelector("[data-rte]");
      if (toolbar) {
        toolbar.querySelectorAll("[data-rte-command]").forEach((btn) => {
          btn.addEventListener("mousedown", (event) => event.preventDefault());
          btn.addEventListener("click", () => this.runRteCommand(btn.dataset.rteCommand));
        });

        const blockSelect = toolbar.querySelector("[data-rte-block]");
        if (blockSelect) {
          this.rteBlockSelect = blockSelect;
          blockSelect.addEventListener("change", () => {
            document.execCommand("formatBlock", false, blockSelect.value);
            this.rteEditor.focus();
            this.syncRteSource();
            this.updateRteToolbarState();
          });
        }
      }

      this.rteEditor.addEventListener("input", () => this.syncRteSource());
      this.rteEditor.addEventListener("blur", () => this.syncRteSource());
      this.rteEditor.addEventListener("keyup", () => this.updateRteToolbarState());
      this.rteEditor.addEventListener("mouseup", () => this.updateRteToolbarState());
      this.syncRteSource();
    }

    updateRteToolbarState() {
      if (!this.rteBlockSelect) return;

      const toolbar = this.querySelector("[data-rte]");
      if (toolbar) {
        toolbar.querySelectorAll("[data-rte-command]").forEach((btn) => {
          const command = btn.dataset.rteCommand;
          if (command === "bold" || command === "italic" || command === "insertUnorderedList" || command === "insertOrderedList") {
            const active = document.queryCommandState(command);
            btn.classList.toggle("is-active", active);
          }
        });
      }

      for (const tag of ["h1", "h2", "h3"]) {
        if (document.queryCommandValue("formatBlock").toLowerCase() === tag) {
          this.rteBlockSelect.value = tag;
          return;
        }
      }
      this.rteBlockSelect.value = "p";
    }

    runRteCommand(command) {
      this.rteEditor.focus();

      if (command === "blockquote") {
        document.execCommand("formatBlock", false, "blockquote");
      } else if (command === "link") {
        const selection = document.getSelection();
        if (!selection || selection.isCollapsed) {
          this.setFormMessage("Select some text first, then add a link.", true);
          return;
        }
        const url = window.prompt("Enter link URL");
        if (!url) return;
        document.execCommand("createLink", false, url);
      } else {
        document.execCommand(command, false, null);
      }

      this.syncRteSource();
      this.updateRteToolbarState();
    }

    syncRteSource() {
      const isEmpty = this.rteEditor.textContent.trim() === "";
      this.rteEditor.classList.toggle("is-empty", isEmpty);
      this.rteSource.value = this.rteToPlainText(this.rteEditor);
    }

    rteToPlainText(root) {
      const lines = [];

      const walkInline = (node) => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent;
        if (node.nodeType !== Node.ELEMENT_NODE) return "";

        const tag = node.tagName.toLowerCase();
        const inner = Array.from(node.childNodes).map(walkInline).join("");

        if (tag === "b" || tag === "strong") return `**${inner}**`;
        if (tag === "i" || tag === "em") return `*${inner}*`;
        if (tag === "u") return `_${inner}_`;
        if (tag === "s" || tag === "strike" || tag === "del") return `~~${inner}~~`;
        if (tag === "a") {
          const href = node.getAttribute("href");
          return href ? `${inner} (${href})` : inner;
        }
        if (tag === "br") return "\n";
        return inner;
      };

      const walkBlock = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (text.trim() !== "") lines.push(text);
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();

        if (tag === "ul") {
          Array.from(node.children).forEach((li) => {
            lines.push(`- ${Array.from(li.childNodes).map(walkInline).join("").trim()}`);
          });
          return;
        }

        if (tag === "ol") {
          Array.from(node.children).forEach((li, index) => {
            lines.push(`${index + 1}. ${Array.from(li.childNodes).map(walkInline).join("").trim()}`);
          });
          return;
        }

        if (tag === "blockquote") {
          const text = Array.from(node.childNodes).map(walkInline).join("").trim();
          if (text !== "") lines.push(`> ${text}`);
          return;
        }

        if (tag === "div" || tag === "p" || /^h[1-6]$/.test(tag)) {
          const text = Array.from(node.childNodes).map(walkInline).join("").trim();
          if (text !== "") lines.push(text);
          return;
        }

        Array.from(node.childNodes).forEach(walkBlock);
      };

      Array.from(root.childNodes).forEach(walkBlock);

      return lines.join("\n").trim();
    }

    bindEvents() {
      if (this.searchInput) {
        this.searchInput.addEventListener("input", () => {
          this.renderQuestions(this.filterQuestions(this.searchInput.value));
        });
      }

      if (this.form) {
        this.form.addEventListener("submit", (event) =>
          this.handleSubmit(event),
        );
      }

      if (this.listEl) {
        this.listEl.addEventListener("click", (event) => {
          const trigger = event.target.closest("[data-answer-now-toggle]");
          if (!trigger) return;
          this.openAnswerPanel(trigger.dataset.rowId);
        });
      }

      if (this.backToListButton) {
        this.backToListButton.addEventListener("click", () =>
          this.closeAnswerPanel(),
        );
      }

      if (this.answerForm) {
        this.answerForm.addEventListener("submit", (event) =>
          this.handleAnswerSubmit(event),
        );
      }

      this.sortButtons.forEach((btn) => {
        btn.addEventListener("click", () => {
          if (btn.dataset.sortType === this.activeSort) return;
          this.activeSort = btn.dataset.sortType;
          this.updateSortButtons();
          this.renderAnswers();
        });
      });

      if (this.answersListEl) {
        this.answersListEl.addEventListener("click", (event) => {
          const helpfulBtn = event.target.closest("[data-helpful]");
          if (helpfulBtn) {
            this.handleHelpfulClick(helpfulBtn);
            return;
          }

          const reportBtn = event.target.closest("[data-report-answer]");
          if (reportBtn) {
            this.handleReportClick(reportBtn);
          }
        });
      }
    }

    async loadQuestions() {
      if (!this.apiUrl || !this.handle) {
        this.setStatus("");
        this.renderQuestions([]);
        return;
      }

      this.setStatus(this.dataset.loadingText || "Loading questions…");

      try {
        const url = `${this.apiUrl}?handle=${encodeURIComponent(this.handle)}`;
        const response = await fetch(url, { credentials: "omit" });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Unknown error");
        }

        this.allQuestions = Array.isArray(data.questions) ? data.questions : [];
        this.setStatus("");
        this.renderQuestions(this.allQuestions);
      } catch (error) {
        console.error("[product-questions] Failed to load questions:", error);
        this.setStatus("");
        this.allQuestions = [];
        this.renderQuestions([]);
      }
    }

    filterQuestions(term) {
      const normalized = term.trim().toLowerCase();
      if (!normalized) return this.allQuestions;

      return this.allQuestions.filter((q) => {
        return (
          q.question.toLowerCase().includes(normalized) ||
          q.answer.toLowerCase().includes(normalized)
        );
      });
    }

    sortByVotes(questions) {
      return [...questions].sort(
        (a, b) => (Number(b.votes) || 0) - (Number(a.votes) || 0),
      );
    }

    renderQuestions(questions) {
      this.updateCount(this.allQuestions.length);

      if (!this.listEl) return;

      this.listEl.innerHTML = "";

      if (!questions.length) {
        this.emptyEl?.removeAttribute("hidden");
        return;
      }

      this.emptyEl?.setAttribute("hidden", "");

      const sorted = this.sortByVotes(questions);

      const fragment = document.createDocumentFragment();
      sorted.forEach((q) =>
        fragment.appendChild(this.buildQuestionItem(q)),
      );
      this.listEl.appendChild(fragment);
    }

    updateCount(count) {
      if (this.countEl) {
        if (count > 0) {
          this.countEl.textContent = `(${count})`;
          this.countEl.removeAttribute("hidden");
        } else {
          this.countEl.setAttribute("hidden", "");
        }
      }

      this.dispatchEvent(
        new CustomEvent("product-questions:count", {
          detail: { count },
          bubbles: true,
        }),
      );
    }

    setStatus(text) {
      if (!this.statusEl) return;
      if (!text) {
        this.statusEl.setAttribute("hidden", "");
        this.statusEl.textContent = "";
      } else {
        this.statusEl.removeAttribute("hidden");
        this.statusEl.textContent = text;
      }
    }

    buildQuestionItem(q) {
      const li = document.createElement("li");
      li.className = "product-questions__item";

      // li.appendChild(this.buildVoteWidget(q));

      const body = document.createElement("div");
      body.className = "product-questions__body";

      const questionEl = document.createElement("p");
      questionEl.className = "product-questions__question-text";
      this.renderTruncatedText(questionEl, `Q: ${q.question}`);

      const answerEl = document.createElement("p");
      answerEl.className = "product-questions__answer-text";
      this.renderTruncatedText(answerEl, `A: ${q.answer}`);

      body.appendChild(questionEl);
      body.appendChild(answerEl);

      if (q.customerName || q.date) {
        const meta = document.createElement("small");
        meta.className = "product-questions__meta";
        // const datePart = q.date ? new Date(q.date).toLocaleDateString() : "";
                const datePart = q.date
          ? new Date(q.date).toLocaleString(undefined, {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })
          : "";
        meta.textContent = [q.customerName, datePart]
          .filter(Boolean)
          .join(" · ");
        body.appendChild(meta);
      }

      const answerNowBtn = document.createElement("button");
      answerNowBtn.type = "button";
      answerNowBtn.className = "product-questions__answer-now";
      answerNowBtn.dataset.answerNowToggle = "";
      answerNowBtn.dataset.rowId = String(q.rowId);
      answerNowBtn.textContent =
        this.dataset.answerNowText || "Answer now";
      body.appendChild(answerNowBtn);

      li.appendChild(body);

      return li;
    }

    renderTruncatedText(el, fullText, limit = 120) {
      if (fullText.length <= limit) {
        el.textContent = fullText;
        return;
      }

      let truncated = fullText.slice(0, limit).trimEnd();
      const lastSpace = truncated.lastIndexOf(" ");
      if (lastSpace > 0) truncated = truncated.slice(0, lastSpace);

      el.textContent = "";

      const shortSpan = document.createElement("span");
      shortSpan.textContent = `${truncated}..... `;

      const fullSpan = document.createElement("span");
      fullSpan.textContent = fullText;
      fullSpan.hidden = true;

      const toggleBtn = document.createElement("button");
      toggleBtn.type = "button";
      toggleBtn.className = "product-questions__read-more";
      toggleBtn.textContent = this.dataset.readMoreText || "Read more";

      toggleBtn.addEventListener("click", () => {
        const expanded = !fullSpan.hidden;
        fullSpan.hidden = expanded;
        shortSpan.hidden = !expanded;
        toggleBtn.textContent = expanded
          ? this.dataset.readMoreText || "Read more"
          : this.dataset.readLessText || "Read less";
      });

      el.appendChild(shortSpan);
      el.appendChild(fullSpan);
      el.appendChild(toggleBtn);
    }

    buildVoteWidget(q) {
      const rowId = q.rowId;
      const votedType = this.getVotedType(rowId);

      const wrapper = document.createElement("div");
      wrapper.className = "product-questions__votes";
      wrapper.dataset.voteWidget = "";
      wrapper.dataset.rowId = String(rowId);

      const upButton = document.createElement("button");
      upButton.type = "button";
      upButton.className = "product-questions__vote-btn product-questions__vote-btn--up";
      upButton.setAttribute("aria-label", "Upvote this question");
      upButton.dataset.voteType = "up";
      upButton.innerHTML = "&#9650;";

      const countEl = document.createElement("span");
      countEl.className = "product-questions__vote-count";
      countEl.dataset.voteCount = "";
      countEl.textContent = `${Number(q.votes) || 0} Votes`;

      const downButton = document.createElement("button");
      downButton.type = "button";
      downButton.className = "product-questions__vote-btn product-questions__vote-btn--down";
      downButton.setAttribute("aria-label", "Downvote this question");
      downButton.dataset.voteType = "down";
      downButton.innerHTML = "&#9660;";

      wrapper.appendChild(upButton);
      wrapper.appendChild(countEl);
      wrapper.appendChild(downButton);

      this.applyVoteState(wrapper, votedType);

      [upButton, downButton].forEach((btn) => {
        btn.addEventListener("click", () =>
          this.handleVoteClick(rowId, btn.dataset.voteType, wrapper),
        );
      });

      return wrapper;
    }

    applyVoteState(wrapper, votedType) {
      const upButton = wrapper.querySelector(
        '[data-vote-type="up"]',
      );
      const downButton = wrapper.querySelector(
        '[data-vote-type="down"]',
      );
      const disabled = Boolean(votedType);

      upButton.disabled = disabled;
      downButton.disabled = disabled;
      upButton.classList.toggle("is-active", votedType === "up");
      downButton.classList.toggle("is-active", votedType === "down");
    }

    getVoteMap() {
      try {
        const raw = localStorage.getItem(VOTE_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (e) {
        return {};
      }
    }

    getVotedType(rowId) {
      const map = this.getVoteMap();
      return map[rowId] || null;
    }

    rememberVote(rowId, type) {
      try {
        const map = this.getVoteMap();
        map[rowId] = type;
        localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify(map));
      } catch (e) {
        // localStorage may be unavailable — safe to ignore.
      }
    }

    async handleVoteClick(rowId, type, wrapper) {
      if (this.getVotedType(rowId)) return;
      if (!this.apiUrl) return;

      const upButton = wrapper.querySelector('[data-vote-type="up"]');
      const downButton = wrapper.querySelector('[data-vote-type="down"]');
      const countEl = wrapper.querySelector("[data-vote-count]");

      upButton.disabled = true;
      downButton.disabled = true;

      const question = this.allQuestions.find(
        (item) => String(item.rowId) === String(rowId),
      );
      const previousVotes = question ? Number(question.votes) || 0 : 0;
      const optimisticVotes =
        type === "up" ? previousVotes + 1 : previousVotes - 1;

      if (countEl) countEl.textContent = `${optimisticVotes} Votes`;
      if (question) question.votes = optimisticVotes;

      try {
        const response = await fetch(this.apiUrl, {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({
            action: "vote",
            rowId: String(rowId),
            type,
          }),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Unknown error");
        }

        const finalVotes = Number(data.votes);
        if (Number.isFinite(finalVotes)) {
          if (countEl) countEl.textContent = `${finalVotes} Votes`;
          if (question) question.votes = finalVotes;
        }

        this.rememberVote(rowId, type);
        this.applyVoteState(wrapper, type);

        const currentTerm = this.searchInput ? this.searchInput.value : "";
        this.renderQuestions(this.filterQuestions(currentTerm));
      } catch (error) {
        console.error("[product-questions] Vote failed:", error);

        if (countEl) countEl.textContent = `${previousVotes} Votes`;
        if (question) question.votes = previousVotes;

        upButton.disabled = false;
        downButton.disabled = false;
      }
    }

    getDedupeKey(email, question) {
      return `${this.handle}::${email.trim().toLowerCase()}::${question.trim().toLowerCase()}`;
    }

    hasAlreadySubmitted(key) {
      try {
        const raw = localStorage.getItem(DEDUPE_STORAGE_KEY);
        const seen = raw ? JSON.parse(raw) : [];
        return Array.isArray(seen) && seen.includes(key);
      } catch (e) {
        return false;
      }
    }

    rememberSubmission(key) {
      try {
        const raw = localStorage.getItem(DEDUPE_STORAGE_KEY);
        const seen = raw ? JSON.parse(raw) : [];
        const updated = Array.isArray(seen) ? seen : [];
        updated.push(key);
        localStorage.setItem(DEDUPE_STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        // localStorage may be unavailable — safe to ignore, server-side check remains authoritative.
      }
    }

    setFormMessage(text, isError) {
      if (!this.formMessage) return;
      if (!text) {
        this.formMessage.setAttribute("hidden", "");
        this.formMessage.textContent = "";
        return;
      }
      this.formMessage.textContent = text;
      this.formMessage.classList.toggle(
        "product-questions__form-message--error",
        Boolean(isError),
      );
      this.formMessage.classList.toggle(
        "product-questions__form-message--success",
        !isError,
      );
      this.formMessage.removeAttribute("hidden");
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting) return;

      const formData = new FormData(this.form);
      const customerName = String(formData.get("customerName") || "").trim();
      const customerEmail = String(formData.get("customerEmail") || "").trim();
      const question = String(formData.get("question") || "").trim();

      if (!customerName || !customerEmail || !question) {
        this.setFormMessage("Please fill in all required fields.", true);
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(customerEmail)) {
        this.setFormMessage("Please enter a valid email address.", true);
        return;
      }

      const dedupeKey = this.getDedupeKey(customerEmail, question);
      if (this.hasAlreadySubmitted(dedupeKey)) {
        this.setFormMessage("You already asked this question.", true);
        return;
      }

      if (!this.apiUrl) {
        this.setFormMessage("This form is not yet configured.", true);
        return;
      }

      this.isSubmitting = true;
      this.submitButton.disabled = true;
      this.setFormMessage("Submitting…", false);

      try {
        const response = await fetch(this.apiUrl, {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({
            productHandle: this.handle,
            productTitle: this.productTitle,
            productUrl: this.productUrl,
            customerName,
            customerEmail,
            question,
          }),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          if (data.error === "duplicate") {
            this.rememberSubmission(dedupeKey);
            this.setFormMessage("You already asked this question.", true);
          } else {
            this.setFormMessage(
              data.error || "Something went wrong. Please try again.",
              true,
            );
          }
          return;
        }

        this.rememberSubmission(dedupeKey);
        this.setFormMessage(
          "Thanks! Your question has been submitted for review.",
          false,
        );
        this.form.reset();
        if (this.rteEditor) {
          this.rteEditor.innerHTML = "";
          this.syncRteSource();
        }

        setTimeout(() => {
          this.isSubmitting = false;
          this.submitButton.disabled = false;
        }, SUBMIT_COOLDOWN_MS);

        return;
      } catch (error) {
        console.error("[product-questions] Submission failed:", error);
        this.setFormMessage(
          "Something went wrong. Please try again later.",
          true,
        );
      }

      this.isSubmitting = false;
      this.submitButton.disabled = false;
    }

    /* ---------------------------------------------------------------- *
     * Answer Now panel
     * ---------------------------------------------------------------- */

    openAnswerPanel(rowId) {
      const question = this.allQuestions.find(
        (item) => String(item.rowId) === String(rowId),
      );
      if (!question || !this.answerPanel || !this.listView) return;

      this.activeQuestion = question;
      this.activeSort = "useful";
      this.updateSortButtons();

      if (this.detailQuestionEl) {
        this.detailQuestionEl.textContent = question.question;
      }

      if (this.detailMetaEl) {
        const datePart = question.date
          ? new Date(question.date).toLocaleString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })
          : "";
        this.detailMetaEl.textContent = ["Asked by " + (question.customerName || "a customer"), datePart]
          .filter(Boolean)
          .join(" on ");
      }

      if (this.answerForm) this.answerForm.reset();
      this.setAnswerFormMessage("", false);

      this.listView.setAttribute("hidden", "");
      if (this.formWrapper) this.formWrapper.setAttribute("hidden", "");
      this.answerPanel.removeAttribute("hidden");
      this.answerPanel.scrollIntoView({ behavior: "smooth", block: "start" });

      this.loadAnswers(question.rowId);
    }

    closeAnswerPanel() {
      if (!this.answerPanel || !this.listView) return;
      this.answerPanel.setAttribute("hidden", "");
      this.listView.removeAttribute("hidden");
      if (this.formWrapper) this.formWrapper.removeAttribute("hidden");
      this.activeQuestion = null;
    }

    updateSortButtons() {
      this.sortButtons.forEach((btn) => {
        btn.classList.toggle("is-active", btn.dataset.sortType === this.activeSort);
      });
    }

    async loadAnswers(rowId) {
      this.activeAnswers = [];
      this.renderAnswers();

      if (!this.apiUrl) return;

      try {
        const url = `${this.apiUrl}?handle=${encodeURIComponent(this.handle)}&questionId=${encodeURIComponent(rowId)}`;
        const response = await fetch(url, { credentials: "omit" });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Unknown error");
        }

        this.activeAnswers = Array.isArray(data.answers) ? data.answers : [];
        this.renderAnswers();
      } catch (error) {
        console.error("[product-questions] Failed to load answers:", error);
        this.activeAnswers = [];
        this.renderAnswers();
      }
    }

    sortAnswers(answers) {
      const sorted = [...answers];

      if (this.activeSort === "useful") {
        sorted.sort((a, b) => (Number(b.helpfulYes) || 0) - (Number(a.helpfulYes) || 0));
      } else if (this.activeSort === "recent") {
        sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
      } else if (this.activeSort === "oldest") {
        sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
      }

      return sorted;
    }

    renderAnswers() {
      const answers = this.activeAnswers || [];

      if (this.answersCountEl) {
        this.answersCountEl.textContent =
          answers.length === 1 ? "1 answer shown" : `${answers.length} answers shown`;
      }

      if (!this.answersListEl) return;

      this.answersListEl.innerHTML = "";

      if (!answers.length) {
        this.answersEmptyEl?.removeAttribute("hidden");
        return;
      }

      this.answersEmptyEl?.setAttribute("hidden", "");

      const fragment = document.createDocumentFragment();
      this.sortAnswers(answers).forEach((a) =>
        fragment.appendChild(this.buildAnswerItem(a)),
      );
      this.answersListEl.appendChild(fragment);
    }

    buildAnswerItem(a) {
      const wrapper = document.createElement("div");
      wrapper.className = "product-questions__answer-item";
      wrapper.dataset.answerId = String(a.answerId);

      const answerEl = document.createElement("p");
      answerEl.className = "product-questions__answer-text";
      answerEl.textContent = a.answer;
      wrapper.appendChild(answerEl);

      const meta = document.createElement("small");
      meta.className = "product-questions__meta";
      const datePart = a.date
        ? new Date(a.date).toLocaleString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })
        : "";
      meta.textContent = [a.customerName, datePart].filter(Boolean).join(" answered on ");
      wrapper.appendChild(meta);

      const helpful = document.createElement("div");
      helpful.className = "product-questions__helpful";

      const helpfulLabel = document.createElement("span");
      helpfulLabel.textContent = "Do you think this answer is useful?";
      helpful.appendChild(helpfulLabel);

      const votedValue = this.getHelpfulVote(a.answerId);

      const yesBtn = document.createElement("button");
      yesBtn.type = "button";
      yesBtn.className = "product-questions__helpful-btn";
      yesBtn.textContent = "YES";
      yesBtn.dataset.helpful = "yes";
      yesBtn.dataset.answerId = String(a.answerId);
      yesBtn.disabled = Boolean(votedValue);
      yesBtn.classList.toggle("is-active", votedValue === "yes");

      const noBtn = document.createElement("button");
      noBtn.type = "button";
      noBtn.className = "product-questions__helpful-btn";
      noBtn.textContent = "NO";
      noBtn.dataset.helpful = "no";
      noBtn.dataset.answerId = String(a.answerId);
      noBtn.disabled = Boolean(votedValue);
      noBtn.classList.toggle("is-active", votedValue === "no");

      helpful.appendChild(yesBtn);
      helpful.appendChild(noBtn);

      const reportBtn = document.createElement("button");
      reportBtn.type = "button";
      reportBtn.className = "product-questions__report";
      reportBtn.dataset.reportAnswer = "";
      reportBtn.dataset.answerId = String(a.answerId);
      reportBtn.textContent = "⚠ Report";

      helpful.appendChild(reportBtn);
      wrapper.appendChild(helpful);

      return wrapper;
    }

    getHelpfulMap() {
      try {
        const raw = localStorage.getItem(HELPFUL_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (e) {
        return {};
      }
    }

    getHelpfulVote(answerId) {
      return this.getHelpfulMap()[answerId] || null;
    }

    rememberHelpfulVote(answerId, value) {
      try {
        const map = this.getHelpfulMap();
        map[answerId] = value;
        localStorage.setItem(HELPFUL_STORAGE_KEY, JSON.stringify(map));
      } catch (e) {
        // localStorage may be unavailable — safe to ignore.
      }
    }

    async handleHelpfulClick(button) {
      const answerId = button.dataset.answerId;
      if (this.getHelpfulVote(answerId)) return;
      if (!this.apiUrl) return;

      const value = button.dataset.helpful;
      const wrapper = button.closest(".product-questions__answer-item");
      const yesBtn = wrapper?.querySelector('[data-helpful="yes"]');
      const noBtn = wrapper?.querySelector('[data-helpful="no"]');

      if (yesBtn) yesBtn.disabled = true;
      if (noBtn) noBtn.disabled = true;

      try {
        const response = await fetch(this.apiUrl, {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({
            action: "answerHelpful",
            answerId: String(answerId),
            value,
          }),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || "Unknown error");
        }

        this.rememberHelpfulVote(answerId, value);
        if (yesBtn) yesBtn.classList.toggle("is-active", value === "yes");
        if (noBtn) noBtn.classList.toggle("is-active", value === "no");
      } catch (error) {
        console.error("[product-questions] Helpful vote failed:", error);
        if (yesBtn) yesBtn.disabled = false;
        if (noBtn) noBtn.disabled = false;
      }
    }

    async handleReportClick(button) {
      const answerId = button.dataset.answerId;
      if (!this.apiUrl || button.disabled) return;

      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = "Reported";

      try {
        const response = await fetch(this.apiUrl, {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({
            action: "reportAnswer",
            answerId: String(answerId),
          }),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || "Unknown error");
        }
      } catch (error) {
        console.error("[product-questions] Report failed:", error);
        button.disabled = false;
        button.textContent = originalText;
      }
    }

    getAnswerDedupeKey(email, questionId) {
      return `${this.handle}::${questionId}::${email.trim().toLowerCase()}`;
    }

    hasAlreadyAnswered(key) {
      try {
        const raw = localStorage.getItem(ANSWER_DEDUPE_STORAGE_KEY);
        const seen = raw ? JSON.parse(raw) : [];
        return Array.isArray(seen) && seen.includes(key);
      } catch (e) {
        return false;
      }
    }

    rememberAnswerSubmission(key) {
      try {
        const raw = localStorage.getItem(ANSWER_DEDUPE_STORAGE_KEY);
        const seen = raw ? JSON.parse(raw) : [];
        const updated = Array.isArray(seen) ? seen : [];
        updated.push(key);
        localStorage.setItem(ANSWER_DEDUPE_STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        // localStorage may be unavailable — safe to ignore, server-side check remains authoritative.
      }
    }

    setAnswerFormMessage(text, isError) {
      if (!this.answerFormMessage) return;
      if (!text) {
        this.answerFormMessage.setAttribute("hidden", "");
        this.answerFormMessage.textContent = "";
        return;
      }
      this.answerFormMessage.textContent = text;
      this.answerFormMessage.classList.toggle(
        "product-questions__form-message--error",
        Boolean(isError),
      );
      this.answerFormMessage.classList.toggle(
        "product-questions__form-message--success",
        !isError,
      );
      this.answerFormMessage.removeAttribute("hidden");
    }

    async handleAnswerSubmit(event) {
      event.preventDefault();

      if (this.isSubmittingAnswer || !this.activeQuestion) return;

      const formData = new FormData(this.answerForm);
      const answer = String(formData.get("answer") || "").trim();
      const customerName = String(formData.get("customerName") || "").trim();
      const customerEmail = String(formData.get("customerEmail") || "").trim();

      if (!answer || !customerName || !customerEmail) {
        this.setAnswerFormMessage("Please fill in all required fields.", true);
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(customerEmail)) {
        this.setAnswerFormMessage("Please enter a valid email address.", true);
        return;
      }

      const questionId = this.activeQuestion.rowId;
      const dedupeKey = this.getAnswerDedupeKey(customerEmail, questionId);
      if (this.hasAlreadyAnswered(dedupeKey)) {
        this.setAnswerFormMessage("You already answered this question.", true);
        return;
      }

      if (!this.apiUrl) {
        this.setAnswerFormMessage("This form is not yet configured.", true);
        return;
      }

      this.isSubmittingAnswer = true;
      this.answerSubmitButton.disabled = true;
      this.setAnswerFormMessage("Submitting…", false);

      try {
        const response = await fetch(this.apiUrl, {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({
            action: "answer",
            productHandle: this.handle,
            questionId: String(questionId),
            customerName,
            customerEmail,
            answer,
          }),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          if (data.error === "duplicate") {
            this.rememberAnswerSubmission(dedupeKey);
            this.setAnswerFormMessage("You already answered this question.", true);
          } else {
            this.setAnswerFormMessage(
              data.error || "Something went wrong. Please try again.",
              true,
            );
          }
          return;
        }

        this.rememberAnswerSubmission(dedupeKey);
        this.setAnswerFormMessage(
          "Thanks! Your answer has been submitted for review.",
          false,
        );
        this.answerForm.reset();

        setTimeout(() => {
          this.isSubmittingAnswer = false;
          this.answerSubmitButton.disabled = false;
        }, SUBMIT_COOLDOWN_MS);

        return;
      } catch (error) {
        console.error("[product-questions] Answer submission failed:", error);
        this.setAnswerFormMessage(
          "Something went wrong. Please try again later.",
          true,
        );
      }

      this.isSubmittingAnswer = false;
      this.answerSubmitButton.disabled = false;
    }
  }

  if (!customElements.get("product-questions")) {
    customElements.define("product-questions", ProductQuestions);
  }
})();

/**
 * Per-product Q&A — "Read more" / "Read less" toggle.
 *
 * Drives PART 1 of snippets/product-questions.liquid, whose entries come
 * from the product's own "custom.product_qa" metafield. Independent of the
 * ProductQuestions custom element above.
 *
 * Both the truncated and the full text are already in the DOM — this only
 * swaps which one is hidden, so the questions stay readable with JS off
 * (the truncated copy stays visible and the button is inert).
 *
 * Delegated from the document so cards added by a theme-editor section
 * re-render keep working without re-binding. The window flag keeps the
 * handler single if this file is ever loaded twice on one page.
 */
(() => {
  if (window.__pqaToggleBound) return;
  window.__pqaToggleBound = true;

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== "function") return;

    const button = target.closest("[data-qa-toggle]");
    if (!button) return;

    const line = button.closest(".pqa__line");
    if (!line) return;

    const body = line.querySelector("[data-qa-body]");
    if (!body) return;

    const short = body.querySelector("[data-qa-short]");
    const full = body.querySelector("[data-qa-full]");
    if (!short || !full) return;

    const expanded = button.getAttribute("aria-expanded") === "true";

    short.hidden = !expanded;
    full.hidden = expanded;
    button.setAttribute("aria-expanded", expanded ? "false" : "true");
    button.textContent = expanded
      ? button.dataset.labelMore || "Read more"
      : button.dataset.labelLess || "Read less";
  });
})();
